import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { TextField, Button, Box, Typography, Alert } from "@mui/material";
import { register, setCredentials } from "../../../features/auth/authSlice";
import {
  fieldProps,
  formPanel,
  loginPaper,
  TitleTypographyLayout,
  TypographyLayout,
  underlineLink,
} from "../AuthStyles";
import theme from "../../../Constants/theme";
import { primaryButtonStyles } from "../../../Constants/buttonStyles";
import ArtPanel from "../ArtPanel";
import { sendSupportRequest } from "../../../utils/emailService";
import { getOrganization } from "../../../features/organization/orgSlice";
import Loader from "../../../Components/Loader";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const OAuthGoogleSignup = () => {
  const query = useQuery();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const first_name = query.get("first_name") || "";
  const last_name = query.get("last_name") || "";
  const email = query.get("email") || "";

  const [organization_name, setOrganization] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Handle session ID from URL parameters and store in localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      localStorage.setItem('neo_session_id', sessionId);

      // Optional: Clean up URL by removing the session_id parameter
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('session_id');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleContinue = async () => {
    if (!organization_name.trim()) {
      setError("Organization name is required");
      return;
    }

    // Get session ID from localStorage and include it in registration data
    const neoSessionId = localStorage.getItem('neo_session_id');
    const payload = {
      first_name,
      last_name,
      email,
      organization_name,
      authentication_method: "google",
      password: "",
      ...(neoSessionId && { neo_session_id: neoSessionId })
    };

    setLoading(true);

    const result = await dispatch(register(payload));

    setLoading(false);

    if (register.rejected.match(result)) {
      setError(result.payload || "Signup failed");
      return;
    }

    if (register.fulfilled.match(result)) {
      // Clear session ID after successful signup
      localStorage.removeItem('neo_session_id');

      // notify support of successful signup
      const isoTime = new Date().toISOString();
      const description =
        `User ${first_name} ${last_name} <${email}> ` +
        `signed up for "${organization_name}" (on ${isoTime}.`;
      await sendSupportRequest({
        name: `${first_name} ${last_name}`,
        fromEmail: email,
        organization: organization_name,
        queryType: "Successful Signup",
        queryDescription: description,
        priority: "Important",
      });

      const token = result.payload.user.user.token;
      const user = result.payload.user.user;

      // save into Redux + localStorage
      dispatch(setCredentials({ user, token }));

      // get org
      const orgId = user?.organization?.["$oid"];
      if (orgId) dispatch(getOrganization(orgId));
      navigate("/dashboard/main");
    } else {
      // payload may be { error: "..." } or bare string
      setError(result.payload?.error || result.payload || "Signup failed");
    }
  };

  const handleGotoLogin = () => {
    // Preserve session ID when navigating to login
    const neoSessionId = localStorage.getItem('neo_session_id');
    if (neoSessionId) {
      navigate(`/auth/login?session_id=${neoSessionId}`);
    } else {
      navigate("/auth/login");
    }
  };

  const handleGotoSignup = () => {
    // Preserve session ID when navigating to regular signup
    const neoSessionId = localStorage.getItem('neo_session_id');
    if (neoSessionId) {
      navigate(`/auth/signup?session_id=${neoSessionId}`);
    } else {
      navigate("/auth/signup");
    }
  };

  if (loading === true) {
    return <Loader />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        bgcolor: theme.main.applicationBackground,
      }}
    >
      <Box sx={formPanel}>
        <Box sx={loginPaper}>
          <Typography sx={TitleTypographyLayout} component="h1" variant="h4">
            Welcome, {first_name}!
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ mb: 3, color: theme.textColors.dark.secondary }}
          >
            Almost there — please provide your organization name to continue.
          </Typography>

          {/* Organization field */}
          <Box sx={{ width: "100%", mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.textColors.dark.primary, mb: 0.5 }}
            >
              Organization Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your organization"
              value={organization_name}
              onChange={(e) => {
                setOrganization(e.target.value);
                if (error) setError("");
              }}
              {...fieldProps}
            />
          </Box>

          {/* General error alert */}
          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            sx={{ ...primaryButtonStyles, my: 2 }}
            onClick={handleContinue}
          >
            Continue
          </Button>

          <Typography variant="caption" sx={TypographyLayout}>
            Already a user?{" "}
            <Box
              onClick={handleGotoLogin}
              sx={underlineLink}
              component={"span"}
            >
              Go to Login
            </Box>
          </Typography>
          <Typography variant="caption" sx={TypographyLayout}>
            Prefer to sign up with email & password?{" "}
            <Box
              onClick={handleGotoSignup}
              sx={underlineLink}
              component={"span"}
            >
              Create an account
            </Box>
          </Typography>
        </Box>
      </Box>
      <ArtPanel />
    </Box>
  );
};

export default OAuthGoogleSignup;